/* eslint-disable react/jsx-no-useless-fragment */
import { MenuCategoriesProps, Template } from '@zeus/shared';
import { BoxComponent } from '../../../../../base/box/Box';
import { TypographyComponent } from '../../../../../base/typography/Typography';
import Item from '../Item';
import { memo } from 'react';
import {
  getLastItemIndexOfCategory,
  getScaleSize,
  isBetweenRange,
  processItemsWithModifiers,
} from '../../../../../utils/utils';
import { TEMPLATE_1_MODIFIERS } from '../../../../../utils/consts';
import { useNormalizedPixels } from '../../../../../hooks/useResize';
import Media from '../../../Media';
import { Grid as GridComponent } from '../../../../../base19/grid/Grid';

interface CategoryProps {
  template: Template;
  category: MenuCategoriesProps;
  categoryIndex?: number;
  development?: boolean;
  timezone?: string;
  scale?: number;
  tenantId?: number;
  fontFamily?: string;
  active?: boolean;
  shouldLoad?: boolean;
}

const Category = ({
  template,
  category,
  categoryIndex,
  development,
  timezone,
  scale = 1,
  tenantId,
  fontFamily: fontFamilyProp,
  active,
  shouldLoad = true,
}: CategoryProps) => {
  const { normalizePixels } = useNormalizedPixels();
  const isDevelopment = development ? true : isBetweenRange(category.schedules, timezone);
  const sortedItems = processItemsWithModifiers(category.items, TEMPLATE_1_MODIFIERS);
  const hasAnyModifier = sortedItems.some((item) => item.hasValidModifiers);
  const lastItemIndex = getLastItemIndexOfCategory(sortedItems) ?? -1;
  const isCategoryEnabled = template?.category?.isEnabled ?? true;
  const isModifier = (hasAnyModifier || sortedItems.length === 0) && template?.itemPrice?.isEnabled;
  const media = template?.media?.mediaContent;
  const isMedia = template?.media?.isEnabled && media?.url;
  const fontFamily = fontFamilyProp || template?.fontFamilyName;

  return (
    <>
      {isDevelopment && (
        <BoxComponent
          style={{
            borderRadius: categoryIndex === 1 ? '33px' : '0px',
            overflow: 'hidden',
            padding: categoryIndex === 1 ? '20px 16px' : '0px',
            flex: 1,
            ...(categoryIndex === 0 ? { paddingRight: '8%' } : {}),
            ...(categoryIndex === 2 ? { paddingLeft: '8%' } : {}),
            ...(template?.background?.image && template?.background?.isEnabled
              ? {
                  backgroundColor: 'transparent',
                }
              : {
                  backgroundColor: template?.background?.color
                    ? template.background.color
                    : categoryIndex === 1
                    ? '#171717'
                    : 'transparent',
                }),
            ...(categoryIndex === 1 && isMedia && template.layoutType === 2
              ? {
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  // rowGap: getScaleSize(normalizePixels(30, 'height'), scale),
                }
              : {}),
          }}
        >
          <BoxComponent overflow={'hidden'} id="category-item-section">
            {(isCategoryEnabled || isModifier) && (
              <>
                {isCategoryEnabled && (
                  <TypographyComponent
                    variant="h3"
                    id=""
                    sx={{
                      color: template?.category?.color ? template?.category?.color : '#FFFFFF',
                      fontWeight: template.category?.fontWeight ?? '400',
                      fontStyle: template.category?.fontStyle,
                      letterSpacing: template.category?.letterSpacing,
                      lineHeight: template.category?.lineHeight,
                      textTransform: 'uppercase',
                      fontFamily: fontFamily,
                      width: '100%',
                      display: '-webkit-box',
                      overflow: 'hidden',
                      '-webkit-line-clamp': '1',
                      '-webkit-box-orient': 'vertical',
                      fontSize: getScaleSize(
                        normalizePixels(46 * Number(template?.category?.textScale ?? 1), 'height'),
                        scale
                      ),
                      marginBottom: getScaleSize(normalizePixels(22, 'height'), scale),
                    }}
                  >
                    {category.name}
                  </TypographyComponent>
                )}

                {isModifier && (
                  <BoxComponent
                    width={'100%'}
                    display="flex"
                    justifyContent="flex-end"
                    sx={{
                      marginBottom:
                        categoryIndex !== 1
                          ? getScaleSize(normalizePixels(2, 'height'), scale)
                          : getScaleSize(normalizePixels(1, 'height'), scale),
                    }}
                  >
                    <BoxComponent width={'35%'}>
                      <GridComponent container>
                        {TEMPLATE_1_MODIFIERS.map((val) => (
                          <GridComponent key={val} item size={6}>
                            <TypographyComponent
                              sx={{
                                color: template?.item?.itemName?.color ? template?.item?.itemName?.color : '#FFFFFF',
                                fontWeight: '400',
                                textAlign: 'center',
                                fontFamily: fontFamily,
                                lineHeight: 'normal',
                                fontSize: getScaleSize(normalizePixels(16, 'height'), scale),
                                textTransform: 'uppercase',
                              }}
                            >
                              {val}
                            </TypographyComponent>
                          </GridComponent>
                        ))}
                      </GridComponent>
                    </BoxComponent>
                  </BoxComponent>
                )}
              </>
            )}

            <BoxComponent display={'flex'} flexDirection={'column'}>
              {sortedItems?.map((item, itemIndex) => (
                <Item
                  key={`${item.id}`}
                  item={item}
                  template={template}
                  development={development}
                  timezone={timezone}
                  categoryIndex={categoryIndex}
                  categoryId={category.id}
                  itemIndex={itemIndex}
                  scale={scale}
                  tenantId={tenantId}
                  hasAnyModifier={hasAnyModifier}
                  lastItemIndex={lastItemIndex}
                  fontFamily={fontFamily}
                />
              ))}
            </BoxComponent>
          </BoxComponent>
          {isMedia && categoryIndex === 1 && template.layoutType === 2 && (
            <BoxComponent
              sx={{ overflow: 'hidden', borderRadius: '33px' }}
              id="temp3-layout-2-image"
              minHeight={getScaleSize(normalizePixels(420, 'height'), scale)}
              height={getScaleSize(normalizePixels(420, 'height'), scale)}
            >
              <Media isDevelopment={development} borderRadius="0px" media={media} height={'100%'} playing={active} />
            </BoxComponent>
          )}
        </BoxComponent>
      )}
    </>
  );
};

export default memo(Category);
